if (!customElements.get('cart-discount')) {
  class CartDiscount extends HTMLElement {
    constructor() {
      super();

      this.addEventListener('submit', this.onSubmit.bind(this));
      this.addEventListener('click', this.onClick.bind(this));
      this.addEventListener('input', this.hideError.bind(this));
    }

    static get messages() {
      return {
        empty: 'Ingresa un código de descuento.',
        duplicate: 'Este código ya está aplicado.',
        invalid: 'Este código no es válido o no aplica a tu carrito.'
      };
    }

    // Every cart render replaces this element, so messages and focus target the fresh copy.
    static find(context) {
      return document.querySelector(`cart-discount[data-context="${context}"]`);
    }

    static focusInput(context) {
      const element = CartDiscount.find(context);
      const input = element ? element.querySelector('.wc-cart-discount__input') : null;
      if (!input) return;

      const drawer = context === 'drawer' ? document.querySelector('cart-drawer') : null;
      if (drawer && drawer.classList.contains('active') && typeof trapFocus === 'function') {
        trapFocus(drawer, input);
      } else {
        input.focus();
      }
    }

    get context() {
      return this.dataset.context;
    }

    get codes() {
      return (this.dataset.codes || '')
        .split(',')
        .map((code) => code.trim())
        .filter(Boolean);
    }

    get input() {
      return this.querySelector('.wc-cart-discount__input');
    }

    onSubmit(event) {
      event.preventDefault();
      if (this.getAttribute('aria-busy') === 'true') return;

      const code = this.input.value.trim();
      if (!code) {
        this.showError(CartDiscount.messages.empty);
        this.input.focus();
        return;
      }

      if (this.codes.some((existing) => existing.toLowerCase() === code.toLowerCase())) {
        this.showError(CartDiscount.messages.duplicate);
        this.input.focus();
        return;
      }

      this.applyCode(code);
    }

    onClick(event) {
      const button = event.target.closest('.wc-cart-discount__remove');
      if (!button || this.getAttribute('aria-busy') === 'true') return;

      this.removeCode(button.dataset.code || '');
    }

    applyCode(code) {
      const context = this.context;
      const existingCodes = this.codes;

      return this.withCart(async (host, sections) => {
        const state = await this.updateDiscounts([...existingCodes, code], sections);
        const applied = (state.discount_codes || [])
          .find((discount) => discount.code && discount.code.toLowerCase() === code.toLowerCase());

        if (applied && applied.applicable !== false) {
          this.render(host, state, sections);
          CartDiscount.focusInput(context);
          return;
        }

        // Shopify keeps unknown/ineligible codes in the cart (applicable: false); drop it again.
        const revertedState = await this.updateDiscounts(existingCodes, sections);
        this.render(host, revertedState, sections);

        const current = CartDiscount.find(context);
        if (!current) return;
        current.input.value = code;
        current.showError(CartDiscount.messages.invalid);
        CartDiscount.focusInput(context);
      });
    }

    removeCode(code) {
      const context = this.context;
      const remainingCodes = this.codes.filter((existing) => existing.toLowerCase() !== code.toLowerCase());

      return this.withCart(async (host, sections) => {
        const state = await this.updateDiscounts(remainingCodes, sections);
        this.render(host, state, sections);
        CartDiscount.focusInput(context);
      });
    }

    async withCart(task) {
      const context = this.context;
      const host = document.querySelector('cart-items') || document.querySelector('cart-drawer-items');

      if (!host || typeof host.getSectionsToRender !== 'function') {
        this.showError(window.cartStrings.error);
        return;
      }

      this.hideError();
      this.setBusy(true);

      try {
        await task(host, host.getSectionsToRender());
      } catch (error) {
        (CartDiscount.find(context) || this).showError(window.cartStrings.error);
      } finally {
        this.setBusy(false);
      }
    }

    updateDiscounts(codes, sections) {
      const body = JSON.stringify({
        discount: codes.join(','),
        sections: sections.map((section) => section.section),
        sections_url: window.location.pathname
      });

      return fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
        .then((response) => {
          if (!response.ok) throw new Error(`Cart update failed (${response.status})`);
          return response.json();
        })
        .then((state) => {
          if (!state || !state.sections) throw new Error('Cart update returned no sections');
          return state;
        });
    }

    render(host, state, sections) {
      host.renderCartState(state, sections);
      if (typeof host.updateLiveRegions === 'function') host.updateLiveRegions(state.item_count);
    }

    setBusy(busy) {
      this.setAttribute('aria-busy', String(busy));
      this.querySelectorAll('input, button').forEach((control) => {
        control.disabled = busy;
      });
    }

    showError(message) {
      const error = this.querySelector('.wc-cart-discount__error');
      const text = error ? error.querySelector('.wc-cart-discount__error-text') : null;
      if (!text) return;

      text.textContent = message;
      error.hidden = false;
      if (this.input) this.input.setAttribute('aria-invalid', 'true');
    }

    hideError() {
      const error = this.querySelector('.wc-cart-discount__error');
      if (!error || error.hidden) return;

      error.hidden = true;
      error.querySelector('.wc-cart-discount__error-text').textContent = '';
      if (this.input) this.input.removeAttribute('aria-invalid');
    }
  }

  customElements.define('cart-discount', CartDiscount);
}
